import axios from 'axios';
import { boreOf, boreRequest, sameBore, type Bore } from './bores';
import { mmScale } from './meshAnnotations';
import { builtBoxOf } from './openaiBlenderApi';
import type { ByPart } from './partModelSize';
import type { PartItem } from './partsRun';
import { faceSizeAxes, type SizeAxis } from './partFaces';
import type { HoleAt, StepHole } from './stepHoles';
import {
  BORE_FILLS, dropEdits, drillJobAll, editsOf, isTrueScale, jobExtents, type Job3D,
} from './threeDApi';
import { makesOwnHole } from './stepHoles';

// DRILLING A PART, FROM SCRATCH — the Holes & covers run's last word on a joint (see JointsRun).
//
// A drill is not undone by the next one: a bore cut where the last Find holes answer put it stays
// in the wood when the next answer moves it. So a part is put back to how it stood before its
// first bore — every drill in its history taken out, the changes made since done again (see
// notice_3d._drop_edits) — and then EVERY hole it has, from every joint, is cut again in batches
// of one Blender run each. What the part holds afterwards is exactly what Find holes says now.
//
// A history the server cannot replay (a texture, a repair or a script made after a bore) refuses
// the first half; the part is then left as it is, and that is said, not drilled over.

// A bore and a plug of one: what drilling a part again from scratch takes back out.
const BORE_OPS: string[] = ['drill', 'drill_all', ...BORE_FILLS];

// At most this many a call — see notice_3d.MAX_DRILL_BATCH.
const BATCH = 8;

// WHAT A DRILLING LEFT UNDONE, with the holes it is about — none where it is about the part (its
// earlier holes could not be taken out, it has no size): a joint shows only its own holes' (see
// JointHoles), and every joint drilling the part the part's.
export type DrillProblem = { holes: string[]; what: string };

// THE DRILL FOUND NO WOOD UNDER THE SPOT — its own words, in mesh_repair_blender.py's bore. Said
// again here with the place and the face's size, so how far off it is reads at a glance.
const OFF_PART = 'there is no material under that spot';
const offPart = (hole: StepHole, dims: { x: number; y: number; z: number }) => {
  const at = (h: HoleAt, axis: SizeAxis) => ('from' in h ? `${h.mm} mm from the ${h.from}` : `centred across its ${axis}`);
  const { u, v } = faceSizeAxes(hole.face);
  const size = { width: dims.x, depth: dims.y, height: dims.z };
  return `Not drilled: the spot given, ${at(hole.u, u)} and ${at(hole.v, v)} of the `
    + `${Math.round(size[u])} × ${Math.round(size[v])} mm ${hole.face} face, is off the part — there is no wood to drill`;
};

export const said = (e: unknown) => (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error)
  || (e instanceof Error ? e.message : String(e));

// IN MILLIMETRES, the way the part viewer scales it: the file's own extents against what the part is
// meant to measure, or its built box. Null where neither is known.
async function inMm(job: Job3D, item: PartItem) {
  const extent = await jobExtents(job.id);
  const mm = extent ? mmScale(extent, item.size, isTrueScale(job), builtBoxOf(job)) : null;
  return extent && mm ? { mm, dims: { x: extent.x * mm, y: extent.y * mm, z: extent.z * mm } } : null;
}

// THE BORES THESE HOLES MAKE IN THIS PART, in its millimetres — the drill's one
// reading. Or what stops all of them, as `what`.
async function boresIn(
  job: Job3D, item: PartItem, holes: StepHole[], items: PartItem[], models: ByPart,
): Promise<
  { mm: number; dims: { x: number; y: number; z: number }; bores: Bore[]; problems: DrillProblem[] } | { what: string }
> {
  const scale = await inMm(job, item);
  if (!scale) return { what: 'it has no measured size to bore against' };
  const { mm, dims } = scale;

  // EACH SPOT ONCE: a pair's two hands are one file, and their holes land on the same spots.
  const problems: DrillProblem[] = [];
  const bores: Bore[] = [];
  for (const hole of holes) {
    const b = boreOf(hole, dims, null, items, models);
    if (!b) return { what: 'the hardware categories have not loaded yet' };
    // OFF THE WOOD, OR ON A LINE THAT DOES NOT WORK: said once already, under the joint's table, by
    // the line that found it.
    if (b === 'wood') continue;
    if (typeof b === 'string') { problems.push({ holes: [hole.id], what: `Not drilled: no ${b}` }); continue; }
    const spot = { face: hole.face, u: b.u, v: b.v, diameter: b.diameter };
    if (bores.some((o) => sameBore({ ...o, face: o.hole.face }, spot))) continue;
    bores.push(b);
  }
  return { mm, dims, bores, problems };
}

export async function drillPart(
  job: Job3D,
  item: PartItem,
  holes: StepHole[],
  items: PartItem[],
  models: ByPart,
): Promise<{ job: Job3D; problems: DrillProblem[] }> {
  const problems: DrillProblem[] = [];
  const whole = (what: string) => ({ job, problems: [{ holes: [], what }] });
  // BACK TO UNDRILLED: every bore this run's history holds, single or batch, and every plug of one.
  const drilled = editsOf(job).flatMap((e, i) => (BORE_OPS.includes(e.op) ? [i] : []));
  if (drilled.length) {
    try {
      job = await dropEdits(job.id, drilled, { forget: true });
    } catch (e) {
      return whole(`its earlier holes could not be taken out, so none were drilled: ${said(e)}`);
    }
  }
  if (!holes.length) return { job, problems };
  const read = await boresIn(job, item, holes, items, models);
  if ('what' in read) return whole(read.what);
  const { mm, dims, bores } = read;
  problems.push(...read.problems);

  // ONE RUN A HOLE NUMBER (Louis 2026-10-29): H103-B and H103-X together, then H104's — each its own
  // entry on the part's history, seen and taken back off on its own. At most BATCH a run all the same.
  const groups = new Map<string, Bore[]>();
  bores.forEach((b) => {
    const n = b.hole.id.split('-')[0];
    groups.set(n, [...(groups.get(n) ?? []), b]);
  });
  // IN THE ORDER OF THEIR IDS, H101 before H102: how the history reads them.
  const byId = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });
  // A POINTED SCREW'S LAST (Louis 2026-10-29), each its own entry: drilled too, since the screw makes
  // that hole as it goes in, but after every other, so the part as it stood before them is on its
  // history and Phase 6 draws it until the screw is driven (see pointedStates).
  const pointed = (g: Bore[]) => g.some((b) => b.hole.via != null && makesOwnHole(items[Number(b.hole.via)]));
  const batches = [...groups.entries()]
    .sort(([a, ga], [b, gb]) => Number(pointed(ga)) - Number(pointed(gb)) || byId(a, b)).map(([, g]) => g)
    .flatMap((g) => Array.from({ length: Math.ceil(g.length / BATCH) },
    (_, k) => g.slice(k * BATCH, (k + 1) * BATCH)));
  for (const batch of batches) {
    try {
      const got = await drillJobAll(job.id, batch.map((b) => boreRequest(b, items)), mm);
      job = got.job;
      got.holes.forEach((r, i) => {
        if (r.ok) return;
        const { hole } = batch[i];
        problems.push({ holes: [hole.id], what: r.error?.startsWith(OFF_PART) ? offPart(hole, dims) : `Not drilled: ${r.error ?? ''}` });
      });
    } catch (e) {
      problems.push({ holes: batch.map((b) => b.hole.id), what: `Not drilled: ${said(e)}` });
    }
  }
  return { job, problems };
}

// WHERE EACH HOLE BORES THIS PART, in its millimetres, as the drill was sent it (see boreRequest) —
// how a history entry from before the bores carried their ids is told which holes it drilled.
export async function holeSpots(
  job: Job3D, item: PartItem, holes: StepHole[], items: PartItem[], models: ByPart,
): Promise<{ hole: StepHole; face: string; u: number; v: number; diameter: number }[]> {
  const scale = await inMm(job, item);
  if (!scale) return [];
  return holes.flatMap((hole) => {
    const b = boreOf(hole, scale.dims, null, items, models);
    return b && typeof b !== 'string' ? [{ hole, face: hole.face, u: b.u, v: b.v, diameter: b.diameter }] : [];
  });
}
